import { useMutation } from "@tanstack/react-query";
import { Button, Modal, Select, Space } from "antd";
import { Roles } from "context/auth";
import { useAuthenticatedRequestCreator } from "hooks/useRequests";
import useUsers from "hooks/useUsers";
import { InitialPageRequest } from "models/common";
import { useState } from "react";

interface BatchReassignmentModalProps {
    assignedUserId: string;
    assignedName: string;
    batchId: string;
    batchName: string;
    loading: boolean;
    onReassignment: () => Promise<void>;
}

const useBatchReassignment = () => {
    const reassign = useAuthenticatedRequestCreator<void, { userId?: string, shipmentBatchId: string }>(
        (data) => ({
            method: "post",
            url: "/shipping/batches/reassign",
            data
        })
    );

    return useMutation(reassign);
}

const BatchReassignmentModal = ({
    assignedUserId,
    assignedName,
    batchId,
    batchName,
    loading,
    onReassignment
}: BatchReassignmentModalProps) => {
    const [open, setOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<string>(assignedUserId);
    const { mutateAsync: reassign, isLoading } = useBatchReassignment();
    const { data: users, isLoading: isLoadingUsers } = useUsers({
        ...InitialPageRequest,
        pageSize: 100,
        filterBy: { 'Roles': [Roles.Staff, Roles.SeniorStaff] },
        sort: ['FirstName', true]
    });

    const cancel = () => {
        setOpen(false);
    }

    const save = async () => {
        if (selectedUserId !== assignedUserId) {
            await reassign({
                userId: selectedUserId,
                shipmentBatchId: batchId
            });
            await onReassignment();
        }

        setOpen(false);
    }

    const unassign = async () => {
        await reassign({ shipmentBatchId: batchId });
        await onReassignment();
        setOpen(false);
    }

    return <>
        <Button type="link" onClick={() => setOpen(true)} size="small">
            {assignedName}
        </Button>
        <Modal
            open={open}
            onOk={save}
            onCancel={cancel}
            closable={false}
            title={batchName}
            confirmLoading={loading || isLoading || isLoadingUsers}
            destroyOnClose
            footer={<Space style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={unassign} danger type="primary">Unassign</Button>
                <Space>
                    <Button onClick={cancel}>Cancel</Button>
                    <Button onClick={save} type="primary">OK</Button>
                </Space>
            </Space>}
        >
            Assigned To: <Select
                size="large"
                style={{ width: '316px', marginLeft: '10px' }}
                value={selectedUserId}
                onChange={setSelectedUserId}
                loading={isLoadingUsers}
                options={users?.data?.map(u => ({
                    label: `${u.firstName} ${u.lastName}`,
                    value: u.id
                }))}
            />
        </Modal>
    </>;
};

export default BatchReassignmentModal;